import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Check, Tag, X, CalendarDays, Users, Minus, Plus } from "lucide-react";
import { api, formatErr, pickLang } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

export default function BookingPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { t, lang } = useLang();
  const [exp, setExp] = useState(null);
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    notes: "",
  });
  const [date, setDate] = useState(params.get("date") || "");
  // keep people as string so the user can clear/edit freely
  const [peopleStr, setPeopleStr] = useState(params.get("people") || "1");
  const [couponCode, setCouponCode] = useState("");
  const [couponData, setCouponData] = useState(null);
  const [couponErr, setCouponErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/experiences/${id}`).then((r) => setExp(r.data)).catch(() => nav("/experiencias"));
  }, [id, nav]);

  // sanitize when computing number
  const peopleNum = Math.max(1, Math.min(50, parseInt(peopleStr) || 1));
  const subtotal = exp ? Number(exp.price) * peopleNum : 0;
  const discount = couponData
    ? (couponData.discount_type === "percent"
      ? (subtotal * Number(couponData.discount_value) / 100)
      : Math.min(subtotal, Number(couponData.discount_value)))
    : 0;
  const total = Math.max(0, subtotal - discount).toFixed(2);

  const dec = () => setPeopleStr(String(Math.max(1, peopleNum - 1)));
  const inc = () => setPeopleStr(String(Math.min(50, peopleNum + 1)));

  const applyCoupon = async () => {
    setCouponErr(""); setCouponData(null);
    if (!couponCode.trim()) return;
    try {
      const res = await api.post("/coupons/validate", { code: couponCode.trim() });
      setCouponData(res.data);
    } catch (e) {
      setCouponErr(t("book.coupon_invalid"));
    }
  };

  const removeCoupon = () => {
    setCouponData(null);
    setCouponCode("");
    setCouponErr("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!date) { setErr(t("detail.select_date_first")); return; }
    setLoading(true);
    try {
      const bres = await api.post("/bookings", {
        experience_id: exp.id,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        booking_date: date,
        num_people: peopleNum,
        notes: form.notes,
      });
      const booking = bres.data;

      const cres = await api.post("/payments/checkout", {
        booking_id: booking.id,
        origin_url: window.location.origin,
        coupon_code: couponData ? couponData.code : null,
      });
      window.location.href = cres.data.url;
    } catch (e) {
      setErr(formatErr(e));
      setLoading(false);
    }
  };

  if (!exp) {
    return <div className="min-h-screen flex items-center justify-center"><div className="bn-eyebrow">{t("common.loading")}</div></div>;
  }

  return (
    <div data-testid="booking-page">
      <Navbar />
      <section className="pt-32 pb-20 bn-section">
        <div className="bn-container px-6 md:px-8">
          <div className="grid md:grid-cols-12 gap-12">
            <div className="md:col-span-7">
              <div className="bn-eyebrow">{lang === "es" ? "Completa tu reserva" : "Complete your booking"}</div>
              <h1 className="bn-heading text-3xl md:text-5xl mt-3">{pickLang(exp, "name", lang)}</h1>
              <p className="mt-3" style={{ color: "var(--bn-text-muted)" }}>{t("book.subtitle")}</p>

              <form onSubmit={submit} className="mt-10 space-y-5" data-testid="booking-form">
                {/* Date + People at top */}
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="bn-eyebrow block mb-2">{t("detail.date")}</label>
                    <div className="relative">
                      <input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        min={new Date().toISOString().split("T")[0]}
                        className="bn-input"
                        required
                        data-testid="booking-date"
                      />
                      <CalendarDays size={16} className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--bn-text-muted)" }} />
                    </div>
                  </div>
                  <div>
                    <label className="bn-eyebrow block mb-2">{t("detail.people")}</label>
                    <div className="flex items-stretch gap-2">
                      <button
                        type="button"
                        onClick={dec}
                        className="w-11 h-11 flex items-center justify-center"
                        style={{ border: "1px solid var(--bn-border)", borderRadius: "var(--bn-button-radius)", background: "var(--bn-surface)", color: "var(--bn-text)" }}
                        aria-label="decrement"
                        data-testid="people-dec"
                      >
                        <Minus size={16} />
                      </button>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={peopleStr}
                        onChange={(e) => {
                          const v = e.target.value.replace(/[^0-9]/g, "");
                          setPeopleStr(v);
                        }}
                        onBlur={() => {
                          if (!peopleStr || parseInt(peopleStr) < 1) setPeopleStr("1");
                          else if (parseInt(peopleStr) > 50) setPeopleStr("50");
                        }}
                        className="bn-input flex-1 text-center"
                        data-testid="booking-people"
                      />
                      <button
                        type="button"
                        onClick={inc}
                        className="w-11 h-11 flex items-center justify-center"
                        style={{ border: "1px solid var(--bn-border)", borderRadius: "var(--bn-button-radius)", background: "var(--bn-surface)", color: "var(--bn-text)" }}
                        aria-label="increment"
                        data-testid="people-inc"
                      >
                        <Plus size={16} />
                      </button>
                      <div className="hidden md:flex items-center pl-3">
                        <Users size={16} style={{ color: "var(--bn-text-muted)" }} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bn-divider" />

                {/* Customer data */}
                <div>
                  <label className="bn-eyebrow block mb-2">{t("book.name")}</label>
                  <input required className="bn-input" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="form-name" />
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="bn-eyebrow block mb-2">{t("book.email")}</label>
                    <input required type="email" className="bn-input" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} data-testid="form-email" />
                  </div>
                  <div>
                    <label className="bn-eyebrow block mb-2">{t("book.whatsapp")}</label>
                    <input required className="bn-input" placeholder="+52..." value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} data-testid="form-phone" />
                  </div>
                </div>
                <div>
                  <label className="bn-eyebrow block mb-2">{t("book.notes")}</label>
                  <textarea rows={3} className="bn-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="form-notes" />
                </div>

                {/* Coupon */}
                <div>
                  <label className="bn-eyebrow mb-2 flex items-center gap-2"><Tag size={12} /> {t("book.coupon")}</label>
                  {!couponData ? (
                    <div className="flex gap-2">
                      <input
                        className="bn-input flex-1"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        placeholder={t("book.coupon_placeholder")}
                        data-testid="coupon-input"
                      />
                      <button type="button" onClick={applyCoupon} className="bn-btn-ghost" data-testid="coupon-apply-btn">{t("book.coupon_apply")}</button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-3" style={{ border: "1px solid var(--bn-accent)", borderRadius: "var(--bn-button-radius)", background: "color-mix(in srgb, var(--bn-accent) 10%, transparent)" }}>
                      <div className="flex items-center gap-2">
                        <Check size={16} style={{ color: "var(--bn-accent)" }} />
                        <span className="bn-heading tracking-wider">{couponData.code}</span>
                        <span className="bn-eyebrow" style={{ color: "var(--bn-text-muted)" }}>
                          {couponData.discount_type === "percent" ? `${couponData.discount_value}% OFF` : `$${couponData.discount_value} OFF`}
                        </span>
                      </div>
                      <button type="button" onClick={removeCoupon} className="opacity-60 hover:opacity-100" data-testid="coupon-remove"><X size={16} /></button>
                    </div>
                  )}
                  {couponErr && <div className="text-xs mt-1" style={{ color: "#b91c1c" }}>{couponErr}</div>}
                </div>

                {err && <div className="text-sm p-3" style={{ background: "rgba(220,38,38,0.1)", color: "#b91c1c", borderRadius: "var(--bn-button-radius)" }}>{err}</div>}

                <button type="submit" disabled={loading} className="bn-btn-primary w-full justify-center" data-testid="submit-booking-btn">
                  {loading ? t("book.processing") : `${t("book.submit")} · $${total} ${exp.currency || "USD"}`}
                </button>
                <p className="text-xs text-center" style={{ color: "var(--bn-text-muted)" }}>{t("book.redirect_note")}</p>
              </form>
            </div>

            {/* Summary */}
            <div className="md:col-span-5">
              <div className="bn-card p-6 md:p-8 md:sticky md:top-28">
                <div className="bn-eyebrow mb-4">{t("book.summary")}</div>
                <div className="aspect-video overflow-hidden" style={{ borderRadius: "var(--bn-card-radius)" }}>
                  <img src={exp.images?.[0]} alt="" className="w-full h-full object-cover" />
                </div>
                <h3 className="bn-heading text-2xl mt-5">{pickLang(exp, "name", lang)}</h3>
                <div className="mt-4 space-y-2 text-sm" style={{ color: "var(--bn-text-muted)" }}>
                  <div className="flex justify-between"><span>{t("detail.date")}</span><span style={{ color: "var(--bn-text)" }}>{date || "—"}</span></div>
                  <div className="flex justify-between"><span>{t("detail.people")}</span><span style={{ color: "var(--bn-text)" }}>{peopleNum}</span></div>
                  <div className="flex justify-between"><span>{t("book.price_per")}</span><span style={{ color: "var(--bn-text)" }}>${exp.price} {exp.currency || "USD"}</span></div>
                  {couponData && (
                    <div className="flex justify-between" style={{ color: "var(--bn-accent)" }}>
                      <span>{t("book.discount")} ({couponData.code})</span>
                      <span>- ${discount.toFixed(2)}</span>
                    </div>
                  )}
                </div>
                <div className="bn-divider my-5" />
                <div className="flex justify-between items-baseline">
                  <span className="bn-eyebrow">{t("detail.total")}</span>
                  <span className="bn-heading text-3xl">${total} {exp.currency || "USD"}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
